// ============================================
// utils/flujos.js — constantes compartidas de los 3 FLUJOS FIJOS (Entrega 1)
// ============================================
// Espejo visual de los seeds del backend (src/migrations/066-flujos-fijos.sql).
// Si el plano de los flujos cambia en backend, actualizar aqui tambien.

import {
  Sparkles, MapPin, ClipboardList, Search, FileText, DollarSign,
  PackageSearch, ShoppingCart, PackageCheck, Wrench, Hammer,
  Handshake, Receipt, CheckCircle2, Drill,
} from 'lucide-react';

// Tipos de trabajo del alta de expediente (primer campo del formulario).
// El backend acepta estos ids y resuelve el workflow (default: instalacion).
export const TIPOS_TRABAJO = [
  {
    id: 'instalacion',
    label: 'Instalacion',
    descripcion: 'CCTV, accesos, alarmas, redes, BeepTV',
    Icono: Drill,
    color: '#185FA5',
  },
  {
    // RENOMBRE-SERVICIOS (2026-07-09): el id interno sigue siendo 'reparacion'
    // (el backend lo normaliza a workflow 'reparaciones'); solo cambia lo visible.
    id: 'reparacion',
    label: 'Servicios',
    descripcion: 'Reparacion, revision, configuracion o ajuste de un equipo existente',
    Icono: Hammer,
    color: '#F97316',
  },
  {
    id: 'venta',
    label: 'Venta',
    descripcion: 'Venta de producto sin instalacion',
    Icono: ShoppingCart,
    color: '#1D9E75',
  },
];

// Icono Lucide por paso (el backend manda el nombre kebab en eo.icono, pero
// aqui lo resolvemos por codigo de paso para no depender de mapeos dinamicos).
export const ICONO_PASO = {
  nuevo_interes: Sparkles,
  visita: MapPin,
  solicitud: ClipboardList,
  diagnostico: Search,
  cotizacion: FileText,
  anticipo: DollarSign,
  preparacion: PackageSearch,
  compra: ShoppingCart,
  almacen: PackageCheck,
  instalacion: Wrench,
  reparacion: Hammer,
  entrega: Handshake,
  facturacion: Receipt,
  cerrado: CheckCircle2,
};

// Visual de los pasos nuevos (mismo color que el seed del catalogo). Los
// componentes viejos los mezclan con su ESTADO_VISUAL para pintar badges.
// MOVIL-2 (2026-09-14): cada paso lleva ademas su ICONO Lucide, para la vista de flujo en celular
// (tipo seguimiento de pedido), donde el nombre no cabe y el icono tiene que ser reconocible solo.
// `icono` es el nombre del componente en lucide-react; se resuelve con el helper IconoLucide.
export const PASO_VISUAL = {
  nuevo_interes: { color: '#93C5FD', label: 'Contexto',    icono: 'ClipboardList', descripcion: 'Datos del interes del cliente' },
  visita:      { color: '#1D4ED8', label: 'Levantamiento', icono: 'MapPin',        descripcion: 'Levantamiento tecnico del sitio (opcional)' },
  solicitud:   { color: '#3B82F6', label: 'Solicitud',    icono: 'MessageSquare',  descripcion: 'Que pide el cliente' },
  diagnostico: { color: '#0EA5E9', label: 'Diagnostico',  icono: 'Stethoscope',    descripcion: 'Diagnostico del equipo' },
  cotizacion:  { color: '#F97316', label: 'Cotizacion',   icono: 'FileText',       descripcion: 'Cotizacion autorizada vinculada' },
  anticipo:    { color: '#FBBF24', label: 'Anticipo',     icono: 'Wallet',         descripcion: 'Pago registrado' },
  preparacion: { color: '#A855F7', label: 'Preparacion',  icono: 'ClipboardCheck', descripcion: 'Checklist de material' },
  compra:      { color: '#C084FC', label: 'Compra',       icono: 'ShoppingCart',   descripcion: 'Faltantes en compra' },
  almacen:     { color: '#15803D', label: 'Almacen',      icono: 'PackageOpen',    descripcion: 'Salida de material' },
  instalacion: { color: '#EA580C', label: 'Instalacion',  icono: 'Wrench',         descripcion: 'Trabajo en sitio' },
  reparacion:  { color: '#DC2626', label: 'Ejecucion del servicio', icono: 'Hammer', descripcion: 'Servicio tecnico realizado' },
  entrega:     { color: '#10B981', label: 'Entrega',      icono: 'Truck',          descripcion: 'Conformidad del cliente' },
  facturacion: { color: '#6366F1', label: 'Facturacion',  icono: 'Receipt',        descripcion: 'Factura emitida' },
  cerrado:     { color: '#16A34A', label: 'Cerrado',      icono: 'CheckCircle2',   descripcion: 'Flujo completado (ganado)' },
};

// Codigos de paso que puede elegir direccion en el override manual y en el
// filtro "Estado:" de la lista (excluye cerrado: eso vive en pestana Ganados).
export const PASOS_FILTRO = [
  'nuevo_interes', 'visita', 'solicitud', 'diagnostico', 'cotizacion',
  'anticipo', 'preparacion', 'compra', 'almacen', 'instalacion',
  'reparacion', 'entrega', 'facturacion',
];

// Etiqueta humana del boton por codigo de accion que devuelve el motor.
export const ACCIONES = {
  editar_expediente:   { label: 'Completar datos' },
  registrar_visita:    { label: 'Registrar levantamiento', registro: 'visita',      titulo: 'Registrar levantamiento', notaObligatoria: false, placeholder: 'Notas tecnicas del levantamiento (opcional)...' },
  registrar_falla:     { label: 'Registrar falla',        registro: 'falla',       titulo: 'Registrar la falla',     notaObligatoria: false, placeholder: 'Que falla reporta el cliente...' },
  registrar_solicitud: { label: 'Registrar solicitud',    registro: 'solicitud',   titulo: 'Registrar la solicitud', notaObligatoria: false, placeholder: 'Que esta pidiendo el cliente...' },
  registrar_diagnostico: { label: 'Registrar diagnostico', registro: 'diagnostico', titulo: 'Registrar diagnostico',  notaObligatoria: false, placeholder: 'Diagnostico del equipo...' },
  registrar_trabajo:   { label: 'Trabajo terminado',      registro: 'trabajo',     titulo: 'Marcar trabajo terminado', notaObligatoria: false, placeholder: 'Notas del trabajo realizado (opcional)...' },
  registrar_reparacion:{ label: 'Servicio terminado',     registro: 'reparacion',  titulo: 'Marcar servicio terminado', notaObligatoria: false, placeholder: 'Notas del servicio realizado (opcional)...' },
  registrar_salida:    { label: 'Registrar salida',       registro: 'salida',      titulo: 'Registrar salida de material', notaObligatoria: false, placeholder: 'Quien se lleva el material (opcional)...' },
  registrar_factura:   { label: 'Registrar factura',      registro: 'factura',     titulo: 'Registrar factura emitida', notaObligatoria: false, placeholder: 'Notas (opcional)...', pideFolio: true },
  registrar_entrega:   { label: 'Confirmar entrega',      registro: 'entrega',     titulo: 'Confirmar entrega con el cliente', notaObligatoria: false, placeholder: 'Como quedo el cliente (opcional)...' },
  registrar_pago:      { label: 'Registrar pago' },
  ver_cotizaciones:    { label: 'Ver cotizaciones' },
  enviar_almacen:      { label: 'Enviar a almacen' },
  checklist_material:  { label: 'Revisar material' },
  faltantes_recibidos: { label: 'Traer del almacén los faltantes' },
  continuar:           { label: 'Continuar al siguiente paso' },
};
